import { useState } from "react";
import { Check, ChevronRight, Home, Building2, Loader2, Wrench, RefreshCw, Sparkles, Sun, FileText, AlertTriangle, Calendar, Clock, Search, CheckCircle, Thermometer } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import OptionGroup from "./OptionGroup";
import { CLIM_FIELDS, PV_FIELDS, buildClimQualification, buildPVQualification } from "./verticalFields";
import { toast } from "sonner";
import { canSubmitNow, markSubmitted } from "./submitGuard";

const PROJECT_TYPES = [
  { id: "reparation", label: "Réparation toiture", icon: Wrench, vertical: "toiture" },
  { id: "remplacement", label: "Remplacement complet", icon: RefreshCw, vertical: "toiture" },
  { id: "nettoyage", label: "Nettoyage & traitement", icon: Sparkles, vertical: "toiture" },
  { id: "solaire", label: "Panneaux solaires", icon: Sun, vertical: "photovoltaique" },
  { id: "clim", label: "Climatisation / Pompe à chaleur air-air", icon: Thermometer, vertical: "climatisation" },
  { id: "autre", label: "Autre", icon: FileText, vertical: "general" },
];

const PROPERTY_TYPES = [
  { id: "maison", label: "Maison", icon: Home },
  { id: "appartement", label: "Appartement", icon: Building2 },
  { id: "immeuble", label: "Immeuble", icon: Building2 },
  { id: "commercial", label: "Bâtiment commercial", icon: Building2 },
];

const SURFACE_OPTIONS = [
  "Moins de 50m²",
  "50 à 100m²",
  "100 à 200m²",
  "Plus de 200m²",
];

const TIMING_OPTIONS = [
  { id: "urgent", label: "C'est urgent (fuite, dégât)", icon: AlertTriangle, color: "text-red-500" },
  { id: "mois", label: "Dans le mois", icon: Calendar, color: "text-amber-500" },
  { id: "trimestre", label: "Dans les 3 mois", icon: Clock, color: "text-blue-500" },
  { id: "renseigne", label: "Je me renseigne", icon: Search, color: "text-slate-500" },
];

const LeadForm = ({ onSubmit }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [vertical, setVertical] = useState("");
  const [qual, setQual] = useState({});
  const [formData, setFormData] = useState({
    type_projet: "",
    type_bien: "",
    surface: "",
    urgence: "",
    prenom: "",
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    code_postal: "",
    rgpd_consent: false,
  });

  const flow = vertical === "climatisation" ? "clim" : vertical === "photovoltaique" ? "pv" : "toiture";
  const qualFields = flow === "clim" ? CLIM_FIELDS : flow === "pv" ? PV_FIELDS : null;
  const stepLabels = qualFields
    ? ["Projet", "Votre projet", "Contact"]
    : ["Projet", "Bien", "Délai", "Contact"];
  const totalSteps = stepLabels.length;
  const contactStep = totalSteps;

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateQual = (field, value) => {
    setQual((prev) => ({ ...prev, [field]: value }));
  };

  const handleProjectSelect = (projectId) => {
    const project = PROJECT_TYPES.find((p) => p.id === projectId);
    setVertical(project.vertical);
    setQual({});
    setFormData((prev) => ({
      ...prev,
      type_projet: project.label,
      type_bien: "",
      surface: "",
      urgence: "",
    }));
    setTimeout(() => setStep(2), 300);
  };

  const handlePropertySelect = (propertyId) => {
    const property = PROPERTY_TYPES.find((p) => p.id === propertyId);
    updateField("type_bien", property?.label || "");
  };

  const handleTimingSelect = (timingId) => {
    const timing = TIMING_OPTIONS.find((t) => t.id === timingId);
    updateField("urgence", timing?.label || "");
    setTimeout(() => setStep(contactStep), 300);
  };

  const canProceedStep2 = qualFields
    ? qualFields.every((f) => qual[f.field])
    : formData.type_bien && formData.surface;

  const canSubmit =
    formData.prenom &&
    formData.nom &&
    formData.telephone &&
    formData.email &&
    formData.adresse &&
    formData.code_postal &&
    formData.rgpd_consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    if (!canSubmitNow()) {
      toast.info("Votre demande a déjà été envoyée. Merci de patienter une minute.");
      return;
    }

    setLoading(true);
    try {
      let qualification = {};
      if (flow === "clim") qualification = buildClimQualification(qual);
      else if (flow === "pv") qualification = buildPVQualification(qual);

      await onSubmit({
        ...formData,
        ...qualification,
        vertical,
        source: "google_ads",
      });
      markSubmitted();
    } catch {
      // Error handled in parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="form" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Demandez votre devis gratuit
          </h2>
          <p className="text-base sm:text-lg text-slate-600">
            Répondez à quelques questions, nous vous rappelons sous 24h
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {stepLabels.map((label, index) => {
              const s = index + 1;
              return (
                <div
                  key={s}
                  className={`flex items-center gap-2 transition-all duration-300 ${
                    step >= s ? "text-amber-600" : "text-slate-400"
                  }`}
                >
                  <div
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm sm:text-base transition-all duration-300 ${
                      step > s
                        ? "bg-green-500 text-white"
                        : step === s
                        ? "bg-amber-500 text-white"
                        : "bg-slate-200 text-slate-500"
                    }`}
                    data-testid={`step-indicator-${s}`}
                  >
                    {step > s ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : s}
                  </div>
                  <span className="hidden md:inline text-xs sm:text-sm font-medium">
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all duration-500 ease-out"
              style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-5 sm:p-8 lg:p-10">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Project Type */}
            {step === 1 && (
              <div className="animate-fade-in" data-testid="form-step-1">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                  Quel est votre projet ?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {PROJECT_TYPES.map((project) => {
                    const IconComponent = project.icon;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => handleProjectSelect(project.id)}
                        className={`form-card p-4 sm:p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                          formData.type_projet === project.label
                            ? "border-amber-500 bg-amber-50"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        data-testid={`project-${project.id}`}
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                        </div>
                        <span className="font-medium text-slate-900 text-sm sm:text-base">{project.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2 (clim/pv): Vertical Qualification */}
            {step === 2 && qualFields && (
              <div className="animate-fade-in space-y-6" data-testid="form-step-qualification">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                  Votre projet {flow === "clim" ? "climatisation" : "photovoltaïque"}
                </h3>

                {qualFields.map((f) => (
                  <OptionGroup
                    key={f.field}
                    label={f.label}
                    options={f.options}
                    value={qual[f.field] || ""}
                    onChange={(v) => updateQual(f.field, v)}
                    testPrefix={f.testPrefix}
                  />
                ))}

                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    data-testid="step-2-back"
                  >
                    Retour
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={!canProceedStep2}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="step-2-next"
                  >
                    Continuer
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2 (toiture): Property Details */}
            {step === 2 && !qualFields && (
              <div className="animate-fade-in space-y-6 sm:space-y-8" data-testid="form-step-2">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                  Votre bien
                </h3>

                {/* Property Type */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">
                    Type de bien
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                    {PROPERTY_TYPES.map((property) => (
                      <button
                        key={property.id}
                        type="button"
                        onClick={() => handlePropertySelect(property.id)}
                        className={`form-card p-3 sm:p-4 rounded-xl border-2 text-center transition-all ${
                          formData.type_bien === property.label
                            ? "border-amber-500 bg-amber-50"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        data-testid={`property-${property.id}`}
                      >
                        <property.icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2 text-slate-600" />
                        <span className="text-xs sm:text-sm font-medium text-slate-900">
                          {property.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Surface */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">
                    Surface approximative
                  </Label>
                  <Select
                    value={formData.surface}
                    onValueChange={(value) => updateField("surface", value)}
                  >
                    <SelectTrigger className="w-full" data-testid="surface-select">
                      <SelectValue placeholder="Sélectionnez la surface" />
                    </SelectTrigger>
                    <SelectContent>
                      {SURFACE_OPTIONS.map((surface) => (
                        <SelectItem key={surface} value={surface}>
                          {surface}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    data-testid="step-2-back"
                  >
                    Retour
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={!canProceedStep2}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="step-2-next"
                  >
                    Continuer
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 (toiture): Timing/Urgency */}
            {step === 3 && !qualFields && (
              <div className="animate-fade-in space-y-6" data-testid="form-step-3">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                  Quand souhaitez-vous réaliser les travaux ?
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {TIMING_OPTIONS.map((timing) => {
                    const IconComponent = timing.icon;
                    return (
                      <button
                        key={timing.id}
                        type="button"
                        onClick={() => handleTimingSelect(timing.id)}
                        className={`form-card p-4 sm:p-5 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                          formData.urgence === timing.label
                            ? "border-amber-500 bg-amber-50"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        data-testid={`timing-${timing.id}`}
                      >
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0`}>
                          <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${timing.color}`} />
                        </div>
                        <span className="font-medium text-slate-900 text-sm sm:text-base">{timing.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-start pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                    data-testid="step-3-back"
                  >
                    Retour
                  </Button>
                </div>
              </div>
            )}

            {/* Contact Info (last step) */}
            {step === contactStep && (
              <div className="animate-fade-in space-y-5 sm:space-y-6" data-testid="form-step-4">
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                  Vos coordonnées
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prenom">Prénom *</Label>
                    <Input
                      id="prenom"
                      type="text"
                      value={formData.prenom}
                      onChange={(e) => updateField("prenom", e.target.value)}
                      placeholder="Jean"
                      required
                      className="mt-1.5"
                      data-testid="input-prenom"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nom">Nom *</Label>
                    <Input
                      id="nom"
                      type="text"
                      value={formData.nom}
                      onChange={(e) => updateField("nom", e.target.value)}
                      placeholder="Dupont"
                      required
                      className="mt-1.5"
                      data-testid="input-nom"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="telephone">Téléphone *</Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => updateField("telephone", e.target.value)}
                    placeholder="0475 12 34 56"
                    required
                    className="mt-1.5"
                    data-testid="input-telephone"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="jean.dupont@email.be"
                    required
                    className="mt-1.5"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="adresse">Adresse *</Label>
                  <Input
                    id="adresse"
                    type="text"
                    value={formData.adresse}
                    onChange={(e) => updateField("adresse", e.target.value)}
                    placeholder="Rue de la Gare 12"
                    required
                    className="mt-1.5"
                    data-testid="input-adresse"
                  />
                </div>

                <div>
                  <Label htmlFor="code_postal">Code postal *</Label>
                  <Input
                    id="code_postal"
                    type="text"
                    value={formData.code_postal}
                    onChange={(e) => updateField("code_postal", e.target.value)}
                    placeholder="1000"
                    required
                    className="mt-1.5"
                    data-testid="input-code-postal"
                  />
                </div>

                {/* RGPD Checkbox */}
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="rgpd"
                    checked={formData.rgpd_consent}
                    onCheckedChange={(checked) => updateField("rgpd_consent", checked)}
                    data-testid="rgpd-checkbox"
                  />
                  <label
                    htmlFor="rgpd"
                    className="text-sm text-slate-600 leading-relaxed cursor-pointer"
                  >
                    J'accepte que mes données soient utilisées pour me recontacter dans
                    le cadre de ma demande de devis.{" "}
                    <a href="/privacy" className="text-amber-600 hover:underline">
                      Politique de confidentialité
                    </a>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={!canSubmit || loading}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-base sm:text-lg font-semibold"
                    data-testid="submit-lead-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      "Recevoir mon devis gratuit"
                    )}
                  </Button>
                  
                  {/* Reassurance */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 text-xs sm:text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Réponse sous 24h ouvrées
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Sans engagement
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Données non revendues
                    </span>
                  </div>
                </div>

                {/* Back Button */}
                <div className="flex justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(contactStep - 1)}
                    data-testid="step-4-back"
                  >
                    Retour
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
